import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Customer } from '@df/session/api/customer';
import { TextStyleOptions } from '@df/ui/atomic/atomic-options';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { CountdownComponent } from '@df/ui/display/countdown.component';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { AbstractCmsBlockComponent } from '../../../../cms/block/abstract-cms-block.component';
import { CUSTOMER_GROUPS, customerGroupVisibilityCmsField, isCustomerAllowed } from '../../../../customer/groups/customer-groups';

export interface IBannerVersionData {
    visibility?: number[];
    background_colour?: string;
    timer_colour?: string;
    time_end: Date;
    text_colour?: string;
    text: string;
    text_style?: string;
    link?: string;
    bold: boolean;
    underline: boolean;
}

export interface IProductCountdownBannerDataContent extends NICmsContentComponent.IData {
    baner_versions: IBannerVersionData[];
    cmsCustomer?: string | number;
}

export const COUNTDOWN_BANNER_ID = 'countdown-baner';

@Component({
    selector: 'countdown-banner',
    templateUrl: './countdown-banner.html',
    standalone: true,
    imports: [CommonModule, CountdownComponent, HrefComponent, ICmsClientModule, TranslatePipe],
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsComponent<PageComponentCountdownBannerComponent>({
    id: COUNTDOWN_BANNER_ID,
    block: {
        url: COUNTDOWN_BANNER_ID
    },
    onDemandOnly: true,
    name: 'Countdown Banner',
    fields: [
        <NICmsContentField.IChoiceField>{
            ...customerGroupVisibilityCmsField(CUSTOMER_GROUPS, false),
            key: 'cmsCustomer',
            title: 'CMS customer',
            description: 'Select customer to preview countdown banner in that variant'
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'baner_versions',
            title: 'Versions Of Baner',
            itemName: 'Version',
            itemNameField: 'visibility',
            description: 'First version that matches current customer is used for display.',
            defaultSize: 1,
            fields: [
                {
                    ...customerGroupVisibilityCmsField(CUSTOMER_GROUPS),
                    required: true
                },
                <NICmsContentField.IField>{
                    type: NICmsContentField.EFieldType.colorPicker,
                    key: 'background_colour',
                    title: 'Background Colour'
                },
                <NICmsContentField.IField>{
                    type: NICmsContentField.EFieldType.colorPicker,
                    key: 'timer_colour',
                    title: 'Timer Colour'
                },
                <NICmsContentField.IDatetimeField>{
                    type: NICmsContentField.EFieldType.datetime,
                    key: 'time_end',
                    title: 'End Time',
                    dateTime: true,
                    required: true
                },
                <NICmsContentField.IField>{
                    type: NICmsContentField.EFieldType.colorPicker,
                    key: 'text_colour',
                    title: 'Text Colour'
                },
                <NICmsContentField.IField>{
                    type: NICmsContentField.EFieldType.text,
                    key: 'text',
                    title: 'Text',
                    required: true
                },
                <NICmsContentField.IChoiceField>{
                    type: NICmsContentField.EFieldType.choice,
                    key: 'text_style',
                    title: 'Text Style',
                    options: TextStyleOptions
                },
                <NICmsContentField.IField>{
                    type: NICmsContentField.EFieldType.text,
                    key: 'link',
                    title: 'Link'
                },
                <NICmsContentField.IField>{
                    type: NICmsContentField.EFieldType.toggle,
                    key: 'bold',
                    title: 'Bold',
                    default: false
                },
                <NICmsContentField.IField>{
                    type: NICmsContentField.EFieldType.toggle,
                    key: 'underline',
                    title: 'Underline',
                    default: false
                }
            ]
        }
    ]
})
export class PageComponentCountdownBannerComponent extends AbstractCmsBlockComponent implements NICmsContentComponent.IComponent, OnInit {
    @ICmsContent({
        type: 'blockContent'
    })
    override content!: IProductCountdownBannerDataContent;

    protected customer = inject(Customer);
    protected destroyRef = inject(DestroyRef);

    override ngOnInit() {
        super.ngOnInit();
        this.customer.model.obs.status.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.detectChanges());
    }

    get bannerDataForCurrentClient(): IBannerVersionData {
        return this.content.baner_versions?.find((data: IBannerVersionData) => {
            if (this.client.isCms && this.content.cmsCustomer) {
                return !!data.visibility?.find(id => id === this.customerGroup);
            }

            return isCustomerAllowed(data.visibility, this.customer.model.data.group_id);
        });
    }

    private get customerGroup(): number {
        if (this.client.isCms && this.content.cmsCustomer) {
            return Number(this.content.cmsCustomer);
        }

        return this.customer.model.data.group_id;
    }
}
